import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import SafeIcon from '../common/SafeIcon';
import { useQuestionnaire } from '../context/QuestionnaireContext';
import { generateGameplan } from '../utils/gameplanGenerator';

const { FiDownload, FiPrinter, FiRefreshCw, FiTarget, FiCalendar, FiTrendingUp, FiCheckCircle } = FiIcons;

function ResultsPage() {
  const navigate = useNavigate();
  const { responses, resetQuestionnaire } = useQuestionnaire();
  const [activeTab, setActiveTab] = useState('overview');
  const [isGenerating, setIsGenerating] = useState(false);
  
  const gameplan = generateGameplan(responses);

  const handleRestart = () => {
    resetQuestionnaire();
    navigate('/');
  };

  const handleDownload = async () => {
    setIsGenerating(true);
    
    try {
      // Create a new jsPDF instance
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let yPosition = 20;

      // Helper function to add a new page if needed
      const checkPageBreak = (requiredSpace = 20) => {
        if (yPosition + requiredSpace > pageHeight - 20) {
          doc.addPage();
          yPosition = 20;
          return true;
        }
        return false;
      };

      // Helper function to add text with word wrap
      const addText = (text, fontSize = 12, isBold = false, color = [0, 0, 0]) => {
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', isBold ? 'bold' : 'normal');
        doc.setTextColor(...color);
        
        const lines = doc.splitTextToSize(text, pageWidth - 40);
        checkPageBreak(lines.length * fontSize * 0.35 + 10);
        
        lines.forEach(line => {
          doc.text(line, 20, yPosition);
          yPosition += fontSize * 0.35;
        });
        yPosition += 5;
      };

      // Helper function to add colored section
      const addSection = (title, bgColor = [59, 130, 246]) => {
        checkPageBreak(30);
        
        // Add colored background
        doc.setFillColor(...bgColor);
        doc.rect(20, yPosition - 5, pageWidth - 40, 15, 'F');
        
        // Add title in white
        addText(title, 14, true, [255, 255, 255]);
        yPosition += 5;
      };

      // Helper function to add a card
      const addCard = (title, content, borderColor = [200, 200, 200]) => {
        checkPageBreak(40);
        
        // Card border
        doc.setDrawColor(...borderColor);
        doc.setFillColor(248, 250, 252);
        doc.roundedRect(20, yPosition - 5, pageWidth - 40, 35, 3, 3, 'FD');
        
        // Card title
        yPosition += 5;
        addText(title, 11, true, [31, 41, 55]);
        
        // Card content
        addText(content, 10, false, [75, 85, 99]);
        yPosition += 5;
      };

      // Cover page
      // Header with gradient effect simulation
      doc.setFillColor(59, 130, 246);
      doc.rect(0, 0, pageWidth, 60, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(28);
      doc.setFont('helvetica', 'bold');
      doc.text('2026 Business Gameplan', pageWidth / 2, 35, { align: 'center' });
      
      doc.setFontSize(14);
      doc.setFont('helvetica', 'normal');
      doc.text('Your Personalized 12-Month Action Plan', pageWidth / 2, 45, { align: 'center' });
      
      yPosition = 80;
      doc.setTextColor(0, 0, 0);
      
      // Generated date
      addText(`Generated on ${new Date().toLocaleDateString()}`, 10, false, [107, 114, 128]);
      yPosition += 20;

      // Rule of 5 Ones Section
      addSection('The Rule of 5 Ones', [59, 130, 246]);
      
      const fiveOnesCards = [
        { title: 'Target Market', content: gameplan.fiveOnes.targetMarket },
        { title: 'Offer', content: gameplan.fiveOnes.offer },
        { title: 'Conversion Mechanism', content: gameplan.fiveOnes.conversionMechanism },
        { title: 'Problem Solved', content: gameplan.fiveOnes.problemSolved },
        { title: 'Launch Date', content: gameplan.fiveOnes.launchDate },
        { title: 'Price Point', content: `$${gameplan.fiveOnes.pricePoint}` }
      ];

      fiveOnesCards.forEach((card, index) => {
        // Alternate colors for visual appeal
        const colors = [
          [59, 130, 246], [34, 197, 94], [168, 85, 247],
          [251, 146, 60], [239, 68, 68], [99, 102, 241]
        ];
        addCard(card.title, card.content, colors[index]);
      });

      // Revenue Goals Section
      addSection('Revenue Targets', [34, 197, 94]);
      
      // Revenue metrics in a styled layout
      const revenueCards = [
        { title: 'Monthly Goal', content: `$${gameplan.revenue.monthlyGoal?.toLocaleString()}` },
        { title: 'Daily Traffic Goal', content: gameplan.revenue.dailyTrafficGoal },
        { title: 'Conversion Rate', content: `${gameplan.revenue.conversionRate}%` }
      ];

      revenueCards.forEach(card => {
        addCard(card.title, card.content, [34, 197, 94]);
      });

      // Calculation breakdown
      addCard('Calculation Breakdown', 
        `Monthly Sales Goal: ${gameplan.revenue.monthlySalesGoal} sales\n` +
        `Monthly Traffic Goal: ${gameplan.revenue.monthlyTrafficGoal} visitors\n` +
        `Average Order Value: $${gameplan.revenue.averageOrderValue}`,
        [200, 200, 200]
      );

      // Action Plans Section
      addSection('Action Plan', [168, 85, 247]);
      
      // Daily Actions
      addText('Daily Priority Tasks:', 12, true);
      gameplan.actions.daily.priority.forEach((task, index) => {
        addText(`• ${task}`, 10, false, [75, 85, 99]);
      });
      
      addText('Daily Extra Tasks:', 12, true);
      gameplan.actions.daily.extra.forEach((task, index) => {
        addText(`• ${task}`, 10, false, [75, 85, 99]);
      });

      // Weekly Actions
      addText('Weekly Actions:', 12, true);
      gameplan.actions.weekly.forEach((task, index) => {
        addText(`• ${task}`, 10, false, [75, 85, 99]);
      });

      // Monthly Actions
      addText('Monthly Actions:', 12, true);
      gameplan.actions.monthly.forEach((task, index) => {
        addText(`• ${task}`, 10, false, [75, 85, 99]);
      });

      // Commitment Section
      addSection('Commitment Statement', [251, 146, 60]);
      
      const commitmentItems = [
        { title: 'My Word for 2026', content: gameplan.commitment.word },
        { title: 'How I Will Show Up', content: gameplan.commitment.showUp },
        { title: 'My Non-Negotiable', content: gameplan.commitment.nonNegotiable },
        { title: 'End of Year Vision', content: gameplan.commitment.endOfYear },
        { title: 'My Why', content: gameplan.commitment.why }
      ];

      commitmentItems.forEach(item => {
        addCard(item.title, item.content, [251, 146, 60]);
      });

      // Footer
      checkPageBreak(30);
      doc.setFillColor(243, 244, 246);
      doc.rect(0, pageHeight - 30, pageWidth, 30, 'F');
      
      doc.setTextColor(107, 114, 128);
      doc.setFontSize(10);
      doc.text('Generated by Optimal Marketing and Consulting', pageWidth / 2, pageHeight - 15, { align: 'center' });
      doc.text('© 2026 Business Gameplan. All rights reserved.', pageWidth / 2, pageHeight - 10, { align: 'center' });

      // Save the PDF
      doc.save('2026-business-gameplan.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FiTarget },
    { id: 'goals', label: 'Goals & Metrics', icon: FiTrendingUp },
    { id: 'actions', label: 'Action Plan', icon: FiCalendar },
    { id: 'commitment', label: 'Commitment', icon: FiCheckCircle }
  ];

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header - Visible on Screen & Print */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-green-500 to-blue-600 rounded-full mb-4 no-print">
          <SafeIcon icon={FiCheckCircle} className="text-2xl text-white" />
        </div>
        <h1 className="text-4xl font-bold text-gray-800 mb-2">
          Your 2026 Business Gameplan
        </h1>
        <p className="text-xl text-gray-600 mb-4 no-print">
          Congratulations! Your personalized 12-month action plan is ready.
        </p>
        
      </motion.div>

      {/* Actions - Hidden on Print */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="flex flex-wrap justify-center gap-4 mb-8 no-print"
      >
        <button
          onClick={handleDownload}
          disabled={isGenerating}
          className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <SafeIcon icon={FiDownload} />
          {isGenerating ? 'Generating PDF...' : 'Download PDF'}
        </button>
        <button
          onClick={handlePrint}
          className="inline-flex items-center gap-2 bg-gray-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-gray-700 transition-colors"
        >
          <SafeIcon icon={FiPrinter} />
          Print Plan
        </button>
        <button
          onClick={handleRestart}
          className="inline-flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-purple-700 transition-colors"
        >
          <SafeIcon icon={FiRefreshCw} />
          Start Over
        </button>
      </motion.div>

      {/* Interactive Tabs - Hidden on Print */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-2xl shadow-xl overflow-hidden no-print"
      >
        <div className="border-b border-gray-200">
          <nav className="flex">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 font-semibold transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                <SafeIcon icon={tab.icon} className="text-lg" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-8">
          {activeTab === 'overview' && <OverviewTab gameplan={gameplan} />}
          {activeTab === 'goals' && <GoalsTab gameplan={gameplan} />}
          {activeTab === 'actions' && <ActionsTab gameplan={gameplan} />}
          {activeTab === 'commitment' && <CommitmentTab gameplan={gameplan} />}
        </div>
      </motion.div>

      {/* Print Only View - Shows ALL sections sequentially */}
      <div className="print-only space-y-8">
        <section>
          <h2 className="text-2xl font-bold border-b-2 border-gray-300 pb-2 mb-4">Overview</h2>
          <OverviewTab gameplan={gameplan} />
        </section>
        
        <div className="page-break"></div>
        
        <section>
          <h2 className="text-2xl font-bold border-b-2 border-gray-300 pb-2 mb-4">Goals & Metrics</h2>
          <GoalsTab gameplan={gameplan} />
        </section>

        <div className="page-break"></div>

        <section>
          <h2 className="text-2xl font-bold border-b-2 border-gray-300 pb-2 mb-4">Action Plan</h2>
          <ActionsTab gameplan={gameplan} />
        </section>

        <div className="page-break"></div>

        <section>
          <h2 className="text-2xl font-bold border-b-2 border-gray-300 pb-2 mb-4">Commitment Statement</h2>
          <CommitmentTab gameplan={gameplan} />
        </section>
      </div>
    </div>
  );
}

function OverviewTab({ gameplan }) {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-4 no-print">The Rule of 5 Ones</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          <div className="bg-blue-50 p-6 rounded-xl border border-blue-100">
            <h3 className="font-semibold text-blue-800 mb-2">Target Market</h3>
            <p className="text-gray-700">{gameplan.fiveOnes.targetMarket}</p>
          </div>
          <div className="bg-green-50 p-6 rounded-xl border border-green-100">
            <h3 className="font-semibold text-green-800 mb-2">Offer</h3>
            <p className="text-gray-700">{gameplan.fiveOnes.offer}</p>
          </div>
          <div className="bg-purple-50 p-6 rounded-xl border border-purple-100">
            <h3 className="font-semibold text-purple-800 mb-2">Conversion Mechanism</h3>
            <p className="text-gray-700">{gameplan.fiveOnes.conversionMechanism}</p>
          </div>
          <div className="bg-orange-50 p-6 rounded-xl border border-orange-100">
            <h3 className="font-semibold text-orange-800 mb-2">Problem Solved</h3>
            <p className="text-gray-700">{gameplan.fiveOnes.problemSolved}</p>
          </div>
          <div className="bg-red-50 p-6 rounded-xl border border-red-100">
            <h3 className="font-semibold text-red-800 mb-2">Launch Date</h3>
            <p className="text-gray-700">{gameplan.fiveOnes.launchDate}</p>
          </div>
          <div className="bg-indigo-50 p-6 rounded-xl border border-indigo-100">
            <h3 className="font-semibold text-indigo-800 mb-2">Price Point</h3>
            <p className="text-gray-700">${gameplan.fiveOnes.pricePoint}</p>
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-4 no-print">2025 Insights</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-800 mb-2">Best Month Revenue</h3>
              <p className="text-gray-700">{gameplan.insights.bestMonth}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-800 mb-2">Success Factors</h3>
              <p className="text-gray-700">{gameplan.insights.successFactors}</p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-800 mb-2">Top Content Types</h3>
              <p className="text-gray-700">{gameplan.insights.topContent}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-800 mb-2">Growth Strategies</h3>
              <p className="text-gray-700">{gameplan.insights.growthStrategies}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function GoalsTab({ gameplan }) {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-6 no-print">Revenue Targets</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-xl print:bg-none print:text-black print:border print:border-gray-300">
            <h3 className="font-semibold mb-2">Monthly Goal</h3>
            <p className="text-2xl font-bold">${gameplan.revenue.monthlyGoal?.toLocaleString()}</p>
          </div>
          <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-xl print:bg-none print:text-black print:border print:border-gray-300">
            <h3 className="font-semibold mb-2">Daily Traffic Goal</h3>
            <p className="text-2xl font-bold">{gameplan.revenue.dailyTrafficGoal}</p>
          </div>
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-6 rounded-xl print:bg-none print:text-black print:border print:border-gray-300">
            <h3 className="font-semibold mb-2">Conversion Rate</h3>
            <p className="text-2xl font-bold">{gameplan.revenue.conversionRate}%</p>
          </div>
        </div>

        <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
          <h3 className="font-semibold text-gray-800 mb-4">Calculation Breakdown</h3>
          <div className="space-y-2 text-gray-700">
            <p>Monthly Sales Goal: <strong>{gameplan.revenue.monthlySalesGoal} sales</strong></p>
            <p>Monthly Traffic Goal: <strong>{gameplan.revenue.monthlyTrafficGoal} visitors</strong></p>
            <p>Average Order Value: <strong>${gameplan.revenue.averageOrderValue}</strong></p>
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-4 no-print">Content Performance Goals</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-gray-200 p-6 rounded-xl">
            <h3 className="font-semibold text-gray-800 mb-3">Posting Schedule</h3>
            <p className="text-gray-700 font-medium">{gameplan.content.postingLevel}</p>
            <ul className="mt-3 space-y-1 text-gray-600">
              {gameplan.content.schedule.map((item, index) => (
                <li key={index}>• {item}</li>
              ))}
            </ul>
          </div>
          <div className="bg-white border border-gray-200 p-6 rounded-xl">
            <h3 className="font-semibold text-gray-800 mb-3">Content Strategy</h3>
            <p className="text-gray-700">Focus on high-performing content types:</p>
            <ul className="mt-3 space-y-1 text-gray-600">
              {gameplan.content.topPerformingTypes.map((type, index) => (
                <li key={index}>• {type}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function ActionsTab({ gameplan }) {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-6 no-print">Daily Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-blue-50 p-6 rounded-xl border border-blue-100">
            <h3 className="font-semibold text-blue-800 mb-4">Priority Tasks</h3>
            <ul className="space-y-2">
              {gameplan.actions.daily.priority.map((task, index) => (
                <li key={index} className="flex items-center gap-2 text-gray-700">
                  <SafeIcon icon={FiCheckCircle} className="text-blue-600 text-sm no-print" />
                  {task}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
            <h3 className="font-semibold text-gray-800 mb-4">Extra Tasks</h3>
            <ul className="space-y-2">
              {gameplan.actions.daily.extra.map((task, index) => (
                <li key={index} className="flex items-center gap-2 text-gray-700">
                  <SafeIcon icon={FiCheckCircle} className="text-gray-600 text-sm no-print" />
                  {task}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-6 no-print">Weekly Actions</h2>
        <div className="bg-green-50 p-6 rounded-xl border border-green-100">
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {gameplan.actions.weekly.map((task, index) => (
              <li key={index} className="flex items-center gap-2 text-gray-700">
                <SafeIcon icon={FiCheckCircle} className="text-green-600 text-sm no-print" />
                {task}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-6 no-print">Monthly Actions</h2>
        <div className="bg-purple-50 p-6 rounded-xl border border-purple-100">
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {gameplan.actions.monthly.map((task, index) => (
              <li key={index} className="flex items-center gap-2 text-gray-700">
                <SafeIcon icon={FiCheckCircle} className="text-purple-600 text-sm no-print" />
                {task}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function CommitmentTab({ gameplan }) {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-6 no-print">Your 2026 Commitment</h2>
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-xl border border-blue-100">
            <h3 className="font-semibold text-gray-800 mb-2">My Word for 2026</h3>
            <p className="text-xl text-gray-700 font-medium">{gameplan.commitment.word}</p>
          </div>
          
          <div className="bg-white border border-gray-200 p-6 rounded-xl">
            <h3 className="font-semibold text-gray-800 mb-3">How I Will Show Up</h3>
            <p className="text-gray-700">{gameplan.commitment.showUp}</p>
          </div>
          
          <div className="bg-white border border-gray-200 p-6 rounded-xl">
            <h3 className="font-semibold text-gray-800 mb-3">My Non-Negotiable</h3>
            <p className="text-gray-700">{gameplan.commitment.nonNegotiable}</p>
          </div>
          
          <div className="bg-white border border-gray-200 p-6 rounded-xl">
            <h3 className="font-semibold text-gray-800 mb-3">End of Year Vision</h3>
            <p className="text-gray-700">{gameplan.commitment.endOfYear}</p>
          </div>
          
          <div className="bg-white border border-gray-200 p-6 rounded-xl">
            <h3 className="font-semibold text-gray-800 mb-3">My Why</h3>
            <p className="text-gray-700">{gameplan.commitment.why}</p>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-8 rounded-xl text-center no-print">
        <h3 className="text-xl font-bold mb-4">Ready to Make 2026 Your Best Year Yet?</h3>
        <p className="text-blue-100 mb-4">
          You now have a complete roadmap for success. The key is consistent daily action 
          and staying committed to your plan.
        </p>
        <p className="font-semibold">
          Remember: Progress over perfection. You've got this! 🚀
        </p>
      </div>
    </div>
  );
}

export default ResultsPage;